import { ExportButton } from '@/common/buttons/ExportButton';
import { TitleButton } from '@/common/buttons/TitleButton';
import { Grid } from '@/common/grid/Grid';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

// The grid needs a flat list (filterModelToExpr, CSV export) alongside the grouped list (display),
// so leaves are collected out of the column groups AGG_CALC_TRX_DATA_PLUGIN returns.
const flattenFieldDefs = (fieldDefs) =>
  fieldDefs.flatMap((fieldDef) => (fieldDef.children ? flattenFieldDefs(fieldDef.children) : [fieldDef]));

/**
 * Per requirement GP-39, this provides a list of transactions used in an Aggregate result within a calculation, as well as allowing users to navigate to its inclusion rules
 */
export const AggCalcTrxViewer = () => {
  const { navState, navigateTo } = useNavState();
  const rsrc = useRsrc();
  const module = useModuleFromRoute();

  const [incDataSrc, setIncDataSrc] = useState();
  const [incMode, setIncMode] = useState(true);

  const [title, setTitle] = useState();
  const gridRef = useRef();

  const [fieldDefs, setFieldDefs] = useState();
  const [groupedFieldDefs, setGroupedFieldDefs] = useState();
  const [exportReq, setExportReq] = useState();

  useEffect(() => {
    const viewerReqParam = navState.viewerReqParam;

    rsrc.data('gp', 'AGG_CALC_TRX_DATA_PLUGIN', { ...viewerReqParam, entity: navState.entity }).then((fieldDefs) => {
      setGroupedFieldDefs(fieldDefs);
      setFieldDefs(flattenFieldDefs(fieldDefs));
    });

    rsrc
      .rows('gp', 'INC_DATA_SRC', { expr: ExprUtils.whereParams('id', viewerReqParam.data.incDataSrcId) })
      .then((dataSrc) => {
        setTitle('Trxs for NDC: ' + viewerReqParam.ndc11 + ', Inclusion Data Source: ' + dataSrc[0].name);
        setIncDataSrc(dataSrc[0]);
      });
  }, []);

  const onCellClicked = (event) => {
    const highlightCols = event.colDef?.highlightCols;
    if (!highlightCols || highlightCols.length === 0) {
      return;
    }

    gridRef.current.gridApiRef.current.flashCells({ columns: highlightCols });
  };

  const onGridReady = (params) => {
    const dataSource = {
      getRows: (params) => {
        const exprs = [ExprUtils.filterModelToExpr(params.filterModel, fieldDefs)];

        if (incMode) {
          exprs.push(ExprUtils.is(`_${incDataSrc.code.toLowerCase()}`, true));

          const trxSelPeriod = navState.viewerReqParam.data.trxSelPeriod;
          if (trxSelPeriod) {
            exprs.push(ExprUtils.is(`_${trxSelPeriod.toLowerCase()}`, true));
          }
        }

        const expr = ExprUtils.and(...exprs);

        const reqParam = {
          ...navState.viewerReqParam,
          entity: navState.entity,
          viewerPluginCode: 'AGG_CALC_TRX_VIEWER_PLUGIN',
          operation: 'ROWS',
          endRow: params.endRow,
          expr,
          rowsReq: {
            startRow: params.startRow,
            endRow: params.endRow,
            expr,
            sortModel: params.sortModel,
          },
        };

        buildAndSetExportReq(reqParam);

        rsrc.data(module, 'VIEWER_DATA_PLUGIN', reqParam, navState.entity).then((res) => {
          gridRef.current.onGridSuccess(res, params);
          gridRef.current.resize();
        });

        // Count and totals depend only on the filter, not the scrolled page, so fetch them once
        // per filter (first block) instead of on every infinite-scroll block.
        if (params.startRow === 0) {
          const summaryReqParam = {
            ...navState.viewerReqParam,
            entity: navState.entity,
            viewerPluginCode: 'AGG_CALC_TRX_VIEWER_PLUGIN',
            operation: 'AGGREGATES',
            rowsReq: { expr },
          };

          rsrc.data(module, 'VIEWER_DATA_PLUGIN', summaryReqParam, navState.entity).then((summary) => {
            gridRef.current.setRowCount(summary.count);
            gridRef.current.setTotalsRow(summary.totals);
          });
        }
      },
    };

    gridRef.current.setDataSource(dataSource);
  };

  const buildAndSetExportReq = (reqParam) => {
    const exportReq = {
      timeZoneName: DateUtils.timeZoneName(),
      name: navState.title + ' Export',
      module,
      entity: 'CALC_PROD_RES',
      uiType: 'VIEWER',
      viewerPluginCode: 'AGG_CALC_TRX_VIEWER_PLUGIN',
      viewerReqParam: reqParam,
      fieldDefs,
    };

    setExportReq(exportReq);
  };

  return (
    fieldDefs &&
    incDataSrc &&
    groupedFieldDefs && (
      <>
        <Box
          sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
        >
          <Title icon={fas.faListUl} title={title} />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ToggleButtonGroup
              value={incMode ? 'included' : 'all'}
              exclusive
              onChange={(e, value) => {
                setIncMode(value === 'included');
              }}
            >
              <ToggleButton value="included" data-test-id="toggle-included">
                Included
              </ToggleButton>
              <ToggleButton value="all" data-test-id="toggle-all">
                All
              </ToggleButton>
            </ToggleButtonGroup>
            <TitleButton
              dataTestId={'calc-nav-to-rules'}
              icon={fas.faFilter}
              toolTip="Navigate To Rules"
              onClick={() =>
                navigateTo('gp/inc-rule-tree', { incDataSrcId: navState.viewerReqParam.data.incDataSrcId })
              }
            />
            <ExportButton exportReq={exportReq} />
          </Box>
        </Box>
        <Grid
          key={`${incMode}-${incDataSrc}`} // force grid to remount when incMode or data source changes to reset ag-grid state
          ref={gridRef}
          gridOptions={{ fieldDefs: groupedFieldDefs }}
          events={{ onGridReady, onCellClicked }}
        />
      </>
    )
  );
};
